import { createApi } from "@reduxjs/toolkit/query/react";
import { ApiResponse, Payment, User } from "../../types/apiTypes";
import { DonationSubmission, TimelineEvent, TimelineEventType } from "@/types/donation-types";
import { normalBaseQuery } from "../BaseQuery";

export type PaymentAttemptStatus = "clicked" | "checkout_ready" | "redirected" | "processing" | "review_required" | "succeeded" | "failed" | "abandoned";

export type PaymentAttemptResponse = {
  attempt_id: string;
  status: PaymentAttemptStatus;
  gateway_status?: string;
  failure_reason?: string;
  completed_at?: string;
  reconciliation_checked_at?: string;
  confirmation_source?: "webhook" | "peach_search";
  failed_at?: string;
};

export const userApi = createApi({
  baseQuery: normalBaseQuery,
  tagTypes: ["fav_companies", "donation", "donation-timeline", "search-history", "visit-history"],
  endpoints: (builder) => ({
    getSubscribePaymentLink: builder.mutation<
      ApiResponse<{ checkout_url: string; attempt_id: string }>,
      { plan_id: string }
    >({
      query: ({ plan_id }) => ({
        url: "payment/initiate",
        method: "POST",
        body: { plan_id },
      }),
    }),
    markPaymentRedirected: builder.mutation<ApiResponse<{ attempt_id: string; status: PaymentAttemptStatus }>, { attempt_id: string }>({
      query: ({ attempt_id }) => ({
        url: `payment/attempt/${attempt_id}/redirected`,
        method: "POST",
      }),
    }),
    getMyMailer: builder.query<ApiResponse<User>, { id: string | undefined }>({
      query: ({ id }) => ({
        url: `user/mailer/${id}`,
        method: "GET",
      }),
    }),
    cancelMailing: builder.mutation<ApiResponse<{ message: string }>, { id: string }>({
      query: ({ id }) => ({
        url: `user/mailer/${id}`,
        method: "DELETE",
      }),
    }),

    getMe: builder.query<ApiResponse<{ user: User }>, void>({
      query: () => ({
        url: `user/me`,
        method: "GET",
      }),
    }),
    sendResetPasswordEmail: builder.mutation<ApiResponse<{ message: string }>, { email: string }>({
      query: ({ email }) => {
        return {
          url: "/utils/password",
          method: "POST",
          body: { email },
        };
      },
    }),
    verifyPasswordResetToken: builder.mutation<
      ApiResponse<{ message: string; hash: string }>,
      { token: string; email: string }
    >({
      query: ({ token, email }) => ({
        url: "/utils/password/verify",
        method: "POST",
        body: { email, token },
      }),
    }),
    paymentStatus: builder.query<ApiResponse<PaymentAttemptResponse>, { attempt_id: string }>({
      query: ({ attempt_id }) => ({
        url: `payment/attempt/${attempt_id}`,
        method: "GET",
      }),
    }),
    changePassword: builder.mutation<
      ApiResponse<{ message: string }>,
      { email: string; newPassword: string; hash: string }
    >({
      query: ({ email, newPassword, hash }) => ({
        url: "/utils/password/reset",
        method: "POST",
        body: { email, newPassword, hash },
      }),
    }),
    secureChangePassword: builder.mutation<ApiResponse<{ message: string }>, { password: string; newPassword: string }>(
      {
        query: ({ password, newPassword }) => ({
          url: `/user/password`,
          method: "PUT",
          body: { password, newPassword },
        }),
      }
    ),

    getMyPayments: builder.query<ApiResponse<Payment[]>, void>({
      query: () => ({
        url: "/payment/my-payments",
        method: "GET",
      }),
    }),

    getFavCompanies: builder.query<ApiResponse<{ fav_companies: string[] }>, { companyType: string }>({
      query: ({ companyType }) => ({
        url: `user/fav-company`,
        method: "GET",
        params: { companyType },
      }),
      providesTags: ["fav_companies"],
    }),

    getFavCompaniesWithInfo: builder.query<
      ApiResponse<
        {
          id: string;
          company_name: string;
          market_value_thousands?: number;
          donation_last_2_years?: number;
          logo: string;
          company_type: "Public" | "Private" | "Nasdaq";
          areas_of_interest?: string[];
          contact_person_1?: { name?: string; position?: string; phone?: string; email?: string };
          company_website?: string;
        }[]
      >,
      void
    >({
      query: () => ({
        url: `user/fav-company/with-info`,
        method: "GET",
      }),
      providesTags: ["fav_companies"],
    }),
    getFavVipsWithInfo: builder.query<
      ApiResponse<{
        id: string;
        name: string;
        given_name?: string;
        surname?: string;
        role?: string[];
        essence?: string[];
        resident?: string[];
      }[]>,
      void
    >({
      query: () => ({ url: "user/fav-vip/with-info", method: "GET" }),
      providesTags: ["fav_companies"],
    }),
    getFavHrWithInfo: builder.query<
      ApiResponse<{
        id: string;
        favoriteKey?: string;
        name: string;
        job_title?: string;
        department?: string;
        company_id?: string;
      }[]>,
      void
    >({
      query: () => ({ url: "user/fav-hr/with-info", method: "GET" }),
      providesTags: ["fav_companies"],
    }),
    mailer: builder.mutation<ApiResponse<{ message: string }>, { operation: "subscribe" | "unsubscribe" }>({
      query: ({ operation }) => ({
        url: `user/mailer`,
        method: "PUT",
        body: { operation },
      }),
    }),
    updateUser: builder.mutation<ApiResponse<User>, Partial<User>>({
      query: (data) => ({
        url: "/user/",
        method: "PUT",
        body: {
          ...data,
        },
      }),
    }),

    postFavCompany: builder.mutation<ApiResponse<{ message: string }>, { companyName: string; companyType: string }>({
      query: ({ companyName, companyType }) => ({
        url: `user/fav-company`,
        method: "POST",
        body: { companyName, companyType },
      }),
      invalidatesTags: ["fav_companies"],
    }),
    getMailer: builder.query<ApiResponse<{ mailer: boolean }>, { id: string }>({
      query: ({ id }) => ({
        url: `user/mailer/${id}`,
        method: "GET",
      }),
    }),
    deleteFavCompany: builder.mutation<ApiResponse<{ message: string }>, { companyName: string; companyType: string }>({
      query: ({ companyName, companyType }) => ({
        url: `user/fav-company`,
        method: "DELETE",
        body: { companyName, companyType },
      }),
      invalidatesTags: ["fav_companies"],
    }),
    addNewSubmission: builder.mutation<
      ApiResponse<{ message: string }>,
      { user_id: string; company_id?: string; vip_id?: string; target_type?: "company" | "vip" }
    >({
      query: ({ company_id, vip_id, user_id, target_type }) => ({
        url: `user/donation`,
        method: "POST",
        body: { company_id, vip_id, user_id, target_type },
      }),
      invalidatesTags: ["donation"],
    }),
    getSubmissions: builder.query<ApiResponse<DonationSubmission[]>, void>({
      query: () => ({
        url: `user/donation`,
        method: "GET",
      }),
      transformResponse: (response: ApiResponse<DonationSubmission[]>) => {
        if (response.data) {
          response.data = response.data.map((sub) => ({
            ...sub,
            status: sub.status === ("on_hold" as string) ? "submitted" : sub.status,
            target_type: sub.target_type ?? "company",
          }));
        }
        return response;
      },
      providesTags: ["donation"],
    }),
    updateSubmission: builder.mutation<
      ApiResponse<DonationSubmission>,
      { id: string; data: Partial<DonationSubmission> }
    >({
      query: ({ id, data }) => ({
        url: `user/donation/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["donation", "donation-timeline"],
    }),
    getTimeline: builder.query<ApiResponse<{ timeline: TimelineEvent[] }>, { donationId: string }>({
      query: ({ donationId }) => ({
        url: `user/donation/timeline/${donationId}`,
        method: "GET",
      }),
      providesTags: ["donation-timeline"],
    }),
    addTimelineEvent: builder.mutation<
      ApiResponse<{ message: string }>,
      { donationId: string; title: string; description?: string; type: TimelineEventType }
    >({
      query: ({ donationId, title, description, type }) => ({
        url: `user/donation/timeline/${donationId}`,
        method: "POST",
        body: { title, description, type },
      }),
      invalidatesTags: ["donation-timeline"],
    }),
    deleteSubmission: builder.mutation<ApiResponse<{ message: string }>, { id: string }>({
      query: ({ id }) => ({
        url: `user/donation/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["donation"],
    }),

    recordSearch: builder.mutation<
      ApiResponse<{ ok: boolean }>,
      { fingerprint: string; query: string; kind: string; filters?: string; resultCount: number }
    >({
      query: (body) => ({ url: "user/search-history", method: "POST", body }),
      invalidatesTags: ["search-history"],
    }),

    getSearchHistory: builder.query<
      ApiResponse<
        {
          fingerprint: string;
          query: string;
          kind: string;
          filters?: string;
          lastSearched: string;
          resultCount: number;
          count: number;
        }[]
      >,
      { limit?: number } | void
    >({
      query: (args) => ({
        url: "user/search-history",
        method: "GET",
        params: args ? { limit: args.limit } : undefined,
      }),
      providesTags: ["search-history"],
    }),

    deleteSearchEntry: builder.mutation<
      ApiResponse<{ deleted: boolean }>,
      { fingerprint: string }
    >({
      query: (body) => ({ url: "user/search-history", method: "DELETE", body }),
      invalidatesTags: ["search-history"],
    }),

    clearSearchHistory: builder.mutation<ApiResponse<{ deleted: boolean }>, void>({
      query: () => ({ url: "user/search-history/all", method: "DELETE" }),
      invalidatesTags: ["search-history"],
    }),

    recordVisit: builder.mutation<
      ApiResponse<{ ok: boolean }>,
      { targetId: string; targetType: "company" | "vip"; name: string }
    >({
      query: (body) => ({ url: "user/visit-history", method: "POST", body }),
      invalidatesTags: ["visit-history"],
    }),

    getVisitHistory: builder.query<
      ApiResponse<
        {
          targetId: string;
          targetType: "company" | "vip";
          name: string;
          lastVisited: string;
          visitCount: number;
        }[]
      >,
      { limit?: number } | void
    >({
      query: (args) => ({
        url: "user/visit-history",
        method: "GET",
        params: args ? { limit: args.limit } : undefined,
      }),
      providesTags: ["visit-history"],
    }),

    deleteVisitEntry: builder.mutation<
      ApiResponse<{ deleted: boolean }>,
      { targetId: string; targetType: "company" | "vip" }
    >({
      query: (body) => ({ url: "user/visit-history", method: "DELETE", body }),
      invalidatesTags: ["visit-history"],
    }),

    clearVisitHistory: builder.mutation<ApiResponse<{ deleted: boolean }>, void>({
      query: () => ({ url: "user/visit-history/all", method: "DELETE" }),
      invalidatesTags: ["visit-history"],
    }),

    dismissGuide: builder.mutation<ApiResponse<{ guide_dismissed_version: number }>, { version: number }>({
      query: ({ version }) => ({
        url: "user/guide-dismissed",
        method: "PUT",
        body: { version },
      }),
    }),
  }),
});

export const {
  useGetSubscribePaymentLinkMutation,
  useMarkPaymentRedirectedMutation,
  useGetTimelineQuery,
  useChangePasswordMutation,
  useDeleteSubmissionMutation,
  useAddTimelineEventMutation,
  useVerifyPasswordResetTokenMutation,
  useAddNewSubmissionMutation,
  useUpdateSubmissionMutation,
  useGetSubmissionsQuery,
  useLazyGetSubmissionsQuery,
  useLazyGetTimelineQuery,
  useGetMailerQuery,
  useMailerMutation,
  useUpdateUserMutation,
  useSecureChangePasswordMutation,
  useGetMyPaymentsQuery,
  useGetMeQuery,
  useLazyGetMeQuery,
  useDeleteFavCompanyMutation,
  useGetFavCompaniesQuery,
  usePostFavCompanyMutation,
  useGetFavCompaniesWithInfoQuery,
  useGetFavVipsWithInfoQuery,
  useGetFavHrWithInfoQuery,
  useGetMyMailerQuery,
  useCancelMailingMutation,
  useSendResetPasswordEmailMutation,
  usePaymentStatusQuery,
  useLazyPaymentStatusQuery,
  useRecordSearchMutation,
  useGetSearchHistoryQuery,
  useDeleteSearchEntryMutation,
  useClearSearchHistoryMutation,
  useRecordVisitMutation,
  useGetVisitHistoryQuery,
  useDeleteVisitEntryMutation,
  useClearVisitHistoryMutation,
  useDismissGuideMutation,
} = userApi;
